import clsx from 'clsx';
import { CSSProperties, MouseEvent, useMemo } from 'react';

import { useVideoModalState } from 'features/video-modal';
import {
  setIsLiteracyInsightModalVisible,
  setLiteracyInsightModalInfo,
} from 'features/literacy-insight-modal';
import { useAppDispatch } from 'shared/lib/redux';
import { PlyrVideoUtils } from 'shared/lib/plyr-video-utils';
import { CardCornerLabel } from 'shared/ui/icons';
import {
  getProgramLink,
  removeDuplicates,
  sortByValues,
  useSubjectsFromProductMap,
} from 'shared/lib/utils';
import { OvalButton } from 'shared/ui/buttons';
import { DomainBadge } from 'shared/ui/domain-badge';
import { Asset } from 'shared/api/retail';
import { eventService, XApiEventKeys } from 'shared/api/event-service';
import {
  BOOST_EM_INSIGHT_TYPE,
  BoostEmInsightDynamicContent,
  Content,
} from 'shared/api/insights';

import { getRecommendationEventContextExtension } from '../../../lib';
import { isRecommendationNew } from '../../../model/recommendations.service';
import { LiteracyInsightHeading } from '../../literacy-insight-heading';

import styles from './styles.module.scss';

export type RecommendationsPreviewItemProps = {
  id: string;
  thumbnailFallbackColor: string;
  programAssetId: string;
  insightType: string;
  createdDate: number;
  content: Content;
  dynamicContent?: BoostEmInsightDynamicContent;
  userPrograms?: Asset[];
  startRelevantDate?: number;
  classNames?: string;
};

export const RecommendationsPreviewItem = ({
  id,
  thumbnailFallbackColor,
  programAssetId,
  userPrograms,
  startRelevantDate,
  classNames,
  insightType,
  content,
  dynamicContent,
  createdDate,
}: RecommendationsPreviewItemProps) => {
  const {
    productNames: domains,
    link: contentLink,
    actionType,
    message,
    image,
  } = content;

  const isLiteracyInsight =
    insightType === BOOST_EM_INSIGHT_TYPE && !!dynamicContent;

  const link = isLiteracyInsight
    ? dynamicContent.insightButtonLink
    : contentLink;
  const callToAction = isLiteracyInsight
    ? dynamicContent.insightButtonLabel
    : actionType;
  const text = isLiteracyInsight ? dynamicContent.headline : message;
  const thumbnailSrc = isLiteracyInsight
    ? dynamicContent.thumbnailImgLink
    : image;

  const isNew = isRecommendationNew(startRelevantDate);
  const subjectsMap = useSubjectsFromProductMap(domains);

  const callToActionBtnText = callToAction ? callToAction : 'Open';
  const callToActionBtnAriaLabel = PlyrVideoUtils.isVideo(link)
    ? "View recommendation's video"
    : '';
  const thumbnailStyles: CSSProperties = {
    backgroundImage: thumbnailSrc ? `url(${thumbnailSrc})` : '',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };

  const { showVideoModal } = useVideoModalState();

  const dispatch = useAppDispatch();

  const uniqueDomains = useMemo(() => {
    if (typeof domains === 'object') {
      return sortByValues(removeDuplicates(domains));
    }
    return domains;
  }, [domains]);

  const handleActionBtnXApiEvent = async (
    e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    await eventService.send(XApiEventKeys.openedRecommendation, undefined, {
      result: {
        ...getRecommendationEventContextExtension(e, domains),
        recommendationId: id,
        isDynamic: isLiteracyInsight,
        recommendationSource: 'educator-home',
      },
    });
  };

  const handleActionBtnClick = async (
    e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    if (PlyrVideoUtils.isVideo(link)) {
      e.preventDefault();
      showVideoModal({
        videoSrc: link,
        posterSrc: thumbnailSrc,
      });
    }

    if (isLiteracyInsight && !dynamicContent.insightButtonLink) {
      dispatch(setIsLiteracyInsightModalVisible(true));
      dispatch(
        setLiteracyInsightModalInfo({
          sidebar: dynamicContent.sidebar,
          recommendations: dynamicContent.recommendations,
          learnMoreLink: dynamicContent.learnMoreLink,
          createdDate,
          insightId: id,
        }),
      );
    }

    await handleActionBtnXApiEvent(e);
  };

  const handleDomainBadgeClick = async (
    e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
    domain: string,
  ) => {
    await eventService.send(
      XApiEventKeys.recommendationProductBreadcrumbSelected,
      undefined,
      {
        result: getRecommendationEventContextExtension(e, domain),
      },
    );
  };

  return (
    <div
      className={clsx(styles.root, classNames)}
      data-testid="recommendations-preview-item"
    >
      {isNew && (
        <div className={styles.newLabelWrap}>
          <CardCornerLabel />
        </div>
      )}

      <div className={styles.mainContent}>
        {programAssetId !== 'Static' && !isLiteracyInsight && (
          <div className={styles.domainsList}>
            {Object.entries(uniqueDomains).map(([asset, domain]) => {
              if (asset === 'Static') return null;

              const subject = subjectsMap ? subjectsMap[asset] : undefined;
              const programLink = getProgramLink(asset, userPrograms);

              return (
                <DomainBadge
                  key={domain}
                  onContextMenu={(e) => handleDomainBadgeClick(e, domain)}
                  onClick={(e) => handleDomainBadgeClick(e, domain)}
                  programLink={programLink}
                  programAssetId={asset}
                  domain={domain}
                  subject={subject}
                  domainButtonClassNames={styles.domainBtn}
                />
              );
            })}
          </div>
        )}
        {isLiteracyInsight && (
          <LiteracyInsightHeading header={dynamicContent.header} />
        )}
        <span className={clsx(styles.text)}>{text}</span>
        <OvalButton
          href={link}
          target="_blank"
          classNames={clsx(
            'btn-blue text-decoration-none mt-auto',
            styles.ovalButton,
          )}
          onContextMenu={handleActionBtnXApiEvent}
          onClick={handleActionBtnClick}
          ariaLabel={callToActionBtnAriaLabel}
        >
          {callToActionBtnText}
        </OvalButton>
      </div>

      <div
        style={{ backgroundColor: `${thumbnailFallbackColor}` }}
        className={styles.thumbnailContainer}
      >
        {thumbnailSrc && (
          <div className={styles.thumbnail} style={thumbnailStyles} />
        )}
      </div>
    </div>
  );
};
